import { useState } from 'react';
import { Mail, X, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/useAuthStore';
import { api } from '@/api/client';

export function VerificationBanner() {
  const { t } = useTranslation();
  const { isEmailVerified, setEmailVerified } = useAuthStore();
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  // Don't show if verified or dismissed
  if (isEmailVerified() || dismissed) {
    return null;
  }

  const handleResend = async () => {
    setLoading(true);
    setError('');
    setSuccess(false);

    try {
      const response = await api.post('/resend-verification');

      if (response.alreadyVerified) {
        // User is already verified - update state
        setEmailVerified(true);
        return;
      }

      if (response.success) {
        setSuccess(true);
        // Hide success message after 5 seconds
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setError(response.message || t('verification.resendError'));
      }
    } catch (err: unknown) {
      const errorObj = err as { message?: string };
      setError(errorObj.message || t('verification.resendError'));
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    // Store in sessionStorage so it stays dismissed until new session
    sessionStorage.setItem('verification-banner-dismissed', 'true');
  };

  // Check if previously dismissed in this session
  if (sessionStorage.getItem('verification-banner-dismissed') === 'true') {
    return null;
  }

  return (
    <div className="bg-amber-50 border-b border-amber-200">
      <div className="max-w-7xl mx-auto px-4 py-3 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <div className="flex-shrink-0">
              <Mail className="h-5 w-5 text-amber-600" />
            </div>
            <p className="text-sm font-medium text-amber-800">
              {t('verification.banner')}
            </p>
          </div>

          <div className="flex items-center gap-3">
            {success && (
              <span className="text-sm text-green-600 font-medium">
                {t('verification.resendSuccess')}
              </span>
            )}

            {error && (
              <span className="text-sm text-red-600">
                {error}
              </span>
            )}

            <button
              onClick={handleResend}
              disabled={loading || success}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-amber-800 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t('verification.sending')}
                </>
              ) : (
                t('verification.resend')
              )}
            </button>

            <button
              onClick={handleDismiss}
              className="p-1 text-amber-600 hover:text-amber-800 hover:bg-amber-100 rounded transition-colors"
              title={t('verification.remindLater')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
